import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { secureLocalStorage } from "../../utils/storageUtil";
import articleService from "../../services/articleService";
import Spinner from "../common/Spinner";

const CurrentIssues = () => {
    const navigate = useNavigate();
    const [freeArticles, setFreeArticles] = useState([]);
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        const fetchUserArticles = async (page = 1, limit = 10) => {
            setLoading(true);
            try {
                const result = await articleService.getFreeArticles(page, limit);
                if (result?.success) {
                    setFreeArticles(result?.data?.articles || []);
                } else {
                    setFreeArticles([]);
                }
            } catch (err) {
                console.error("Error fetching free articles:", err);
                setFreeArticles([]);
            } finally {
                setLoading(false);
            }
        };

        fetchUserArticles();
    }, [setLoading]);

    const featuredArticles = useMemo(() => freeArticles.slice(0, 2), [freeArticles]);

    //  Free article open (no auth gating)
    const handleViewArticle = (articleId) => {
        navigate(`/free-article/${articleId}`);
    };

    //  Archive click gating (login/plans/articles)
    const getArchivePath = () => {
        const storedUser = secureLocalStorage.getItem("userData", true);

        if (!storedUser) return "/login";
        return "/allArticlesPage";
    };

    const handleArchiveClick = (e) => {
        e.preventDefault();
        navigate(getArchivePath());
    };

    const getAuthorLabel = (authors = []) => {
        if (!Array.isArray(authors) || authors.length === 0) return "AJMM Editorial";
        const first = authors.find((a) => a?.firstAuthor) || authors[0];
        const name = `${first?.firstName || ""} ${first?.lastName || ""}`.trim();
        return authors.length > 1 ? `${name} et al.` : name;
    };

    const formatDate = (isoString) => {
        if (!isoString) return "";
        const d = new Date(isoString);
        if (Number.isNaN(d.getTime())) return "";
        return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    };

    const getKeywords = (keywords) => {
        if (!Array.isArray(keywords)) return [];
        return keywords.filter(Boolean).slice(0, 3);
    };

    return (
        <>
            {loading ? (
                <div className="flex justify-center py-10">
                    <Spinner size="medium" color="primary" />
                </div>
            )
                : <section className="py-16 md:py-24 bg-white dark:bg-slate-900">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-4">
                            <div>
                                <span className="text-medical-600 dark:text-medical-400 font-bold tracking-wider uppercase text-sm">
                                    Latest Issues
                                </span>
                                <h2 className="font-serif text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mt-2">
                                    Current Research
                                </h2>
                            </div>

                            <Link
                                to={getArchivePath()}
                                onClick={handleArchiveClick}
                                className="flex items-center text-medical-700 dark:text-medical-400 hover:text-medical-900 dark:hover:text-medical-200 font-bold text-lg group"
                            >
                                View All{" "}
                                <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                            </Link>
                        </div>

                        <div className="grid md:grid-cols-2 gap-8 md:gap-10">
                            {featuredArticles.map((article) => {
                                const id = article?._id;
                                const title = article?.title || "Untitled Article";
                                const abstract =
                                    article?.abstract ||
                                    article?.seo?.metaDescription ||
                                    "No abstract available.";

                                const authorLabel = getAuthorLabel(article?.authors);
                                const keywords = getKeywords(article?.keywords);
                                const dateLabel = formatDate(article?.createdAt) || "";

                                return (
                                    <div
                                        key={id || title}
                                        className="group flex flex-col h-full bg-gray-50 dark:bg-slate-800 p-6 md:p-8 rounded-2xl border border-gray-100 dark:border-slate-700 hover:shadow-xl dark:hover:shadow-slate-900/50 transition-all duration-300"
                                    >
                                        <span className="self-start px-3 py-1 bg-white dark:bg-slate-700 text-medical-700 dark:text-medical-300 text-xs font-bold rounded-full mb-6 border border-gray-100 dark:border-slate-600">
                                            FREE ACCESS
                                        </span>

                                        <h3 className="font-serif text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-medical-700 dark:group-hover:text-medical-400 transition-colors">
                                            <button
                                                type="button"
                                                onClick={() => handleViewArticle(id)}
                                                className="text-left"
                                            >
                                                {title}
                                            </button>
                                        </h3>

                                        <div className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                                            <span className="font-semibold">{authorLabel}</span>
                                            {dateLabel ? <span className="mx-2">•</span> : null}
                                            {dateLabel ? <span>{dateLabel}</span> : null}
                                        </div>

                                        <p className="text-gray-600 dark:text-gray-400 mb-6 line-clamp-3 leading-relaxed flex-grow">
                                            {abstract}
                                        </p>

                                        {keywords.length > 0 && (
                                            <div className="flex flex-wrap gap-2 mb-6">
                                                {keywords.map((k) => (
                                                    <span
                                                        key={k}
                                                        className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white dark:bg-slate-700 border border-gray-100 dark:border-slate-600 text-gray-700 dark:text-gray-200"
                                                    >
                                                        {k}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-slate-700">
                                            <div className="text-sm text-gray-500 dark:text-gray-500 font-medium">
                                                {dateLabel}
                                            </div>

                                            <button
                                                type="button"
                                                onClick={() => handleViewArticle(id)}
                                                className="text-medical-600 dark:text-medical-400 font-bold text-sm hover:underline"
                                            >
                                                Read Full Paper
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}

                            {featuredArticles.length === 0 && (
                                <div className="col-span-full text-center py-10 text-gray-600 dark:text-gray-300">
                                    No free articles available right now.
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            }
        </>
    );
};

export default CurrentIssues;
